import React, { Fragment, useCallback } from 'react';
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Transition,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { setSnackbar } from 'flow/actions/app-actions';
import { segmentTrack } from 'flow/actions/segment-actions';
import { updateTicketVisibility } from 'flow/actions/tickets-action';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectTicketVisibility } from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketVisibility } from 'flow/types/Tickets';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import useTicketAction from 'flow/hooks/useTicketAction';
import { ticketWriteWasRefused } from 'flow/utils/ticket-utils';

/**
 * Ordered as the ladder they sit on, least open first. Each tier states what it
 * grants rather than reading as on/off: `public` gives a colleague the same access
 * as a member, which "on" did not say. Holds the key, not the translation, so the
 * list is a constant and the lookup happens where i18next is available.
 */
const ACCESS_OPTIONS: { value: TicketVisibility; labelKey: string }[] = [
    {
        value: 'private',
        labelKey: 'tickets.access.general_access_option_private',
    },
    {
        value: 'restricted',
        labelKey: 'tickets.access.general_access_option_restricted',
    },
    {
        value: 'public',
        labelKey: 'tickets.access.general_access_option_public',
    },
];

interface Props {
    ticketKey: TicketKey;
    canEdit: boolean;
}

/**
 * The tier the ticket grants a colleague who is not a member.
 *
 * The panel is anchored rather than absolutely positioned, so it is portalled out
 * and opening it cannot grow the dialog and give it a scrollbar. It prefers upward,
 * since the select sits near the bottom of the dialog, and flips down by itself
 * when there is no room.
 */
const GeneralAccessSelect = ({ ticketKey, canEdit }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const visibility = useAppSelector((state) =>
        selectTicketVisibility(state, ticketKey),
    );

    const selected = ACCESS_OPTIONS.find(
        (option) => option.value === visibility,
    );

    // No option matches a tier this client cannot name. Saying so beats an empty
    // box, and the select stays usable so the viewer can set a tier it does know.
    const selectedLabel = selected
        ? i18next.t(selected.labelKey)
        : i18next.t('status.unknown');

    const onChange = useCallback(
        (option: { value: TicketVisibility }) => {
            if (option.value === visibility) return;

            // The select renders the stored value, so a rejected change reverts
            // itself. Say so, rather than letting the tier snap back in silence.
            runTicketAction(
                updateTicketVisibility(ticketKey, option.value),
            ).catch((error?: { status?: number }) => {
                // The refusal already opened the Access denied dialog, so the
                // snackbar is for everything else.
                if (ticketWriteWasRefused(error)) return;
                dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            });
            dispatch(
                segmentTrack(TICKET_STRINGS.CHANGE_GENERAL_ACCESS, {
                    component: 'ticket access modal',
                }),
            );
        },
        [dispatch, ticketKey, visibility],
    );

    return (
        <div className="flex flex-col gap-y-2">
            <span className="text-sm font-bold text-gray-900">
                {i18next.t('tickets.access.general_access')}
            </span>

            <Listbox
                value={selected}
                by="value"
                onChange={onChange}
                disabled={!canEdit}
            >
                <div className="relative w-full text-sm/4">
                    <ListboxButton className="hover:darken-10 focus-ring flex h-10 w-full items-center rounded-sm bg-gray-200 ps-3 pe-9 text-start text-gray-900 data-disabled:text-gray-500 data-disabled:hover:brightness-100">
                        {selectedLabel}
                        <div className="absolute inset-y-0 end-0 flex items-center pe-2">
                            <Icon
                                className="size-5 text-gray-600"
                                iconKey="arrow_down"
                                aria-hidden="true"
                            />
                        </div>
                    </ListboxButton>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <ListboxOptions
                            anchor={{ to: 'top start', gap: 4 }}
                            // Same reason as the candidates list: a modal panel
                            // marks the rest of the dialog inert, which puts the
                            // members list and Done out of reach while it is open.
                            modal={false}
                            className="z-50 max-h-60 w-(--button-width) overflow-auto rounded-lg bg-white py-2 shadow-lg focus:outline-hidden dark:bg-gray-300"
                        >
                            {ACCESS_OPTIONS.map((option) => (
                                <ListboxOption
                                    key={option.value}
                                    className={({ focus }) =>
                                        twMerge(
                                            'relative flex cursor-default items-center justify-between py-2 ps-3 pe-3 text-gray-900 select-none',
                                            focus && 'bg-gray-200',
                                        )
                                    }
                                    value={option}
                                >
                                    {({ selected: isSelected }) => (
                                        <>
                                            <span
                                                className={twMerge(
                                                    'truncate',
                                                    isSelected && 'font-medium',
                                                )}
                                            >
                                                {i18next.t(option.labelKey)}
                                            </span>
                                            {isSelected ? (
                                                <Icon
                                                    iconKey="check"
                                                    className="size-5 text-gray-800"
                                                />
                                            ) : null}
                                        </>
                                    )}
                                </ListboxOption>
                            ))}
                        </ListboxOptions>
                    </Transition>
                </div>
            </Listbox>

            <span className="text-xs leading-4 text-gray-500">
                {i18next.t('tickets.access.general_access_description')}
            </span>
        </div>
    );
};

export default GeneralAccessSelect;
